"use client"

import { accessTokenStore, registrationTokenStore } from "./token-store"
import { t } from "@/lib/i18n"
import { leaveBreadcrumb, notify } from "@/lib/observability/bugsnag"

const BASE = process.env.NEXT_PUBLIC_API_BASE ?? ""

/** Which bearer token (if any) to attach to a request. */
type AuthMode = "access" | "registration" | "none"

interface RequestOptions {
  method?: string
  /** JSON body — serialized + Content-Type set automatically. */
  body?: unknown
  /** Multipart body — sent as-is, browser sets the boundary Content-Type. */
  formData?: FormData
  auth?: AuthMode
  signal?: AbortSignal
}

/**
 * Machine-readable code the backend attaches to a ban response so the app can
 * tell a suspension apart from other 403s (unverified contact, staff-only).
 * Must match the backend's AccountSuspendedException::CODE.
 */
export const SUSPENDED_CODE = "account_suspended"

/** Thrown for any non-2xx response. Carries Laravel's validation `errors` map. */
export class ApiError extends Error {
  status: number
  /** Stable backend error code, when present (e.g. "account_suspended"). */
  code?: string
  errors?: Record<string, string[]>

  constructor(
    status: number,
    message: string,
    code?: string,
    errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
    this.errors = errors
  }

  /** First validation message for a field, if any. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0]
  }
}

/** Thrown when the request never reached the server (offline, DNS, CORS). */
export class NetworkError extends Error {
  constructor(message = "Network request failed") {
    super(message)
    this.name = "NetworkError"
  }
}

export type ErrorReason = "offline" | "auth" | "server" | "unknown"

/**
 * Classify a thrown error so the UI can show cause-specific, recoverable copy
 * instead of a generic "something went wrong".
 */
export function errorReason(e: unknown): ErrorReason {
  if (e instanceof NetworkError) return "offline"
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return "offline"
  if (e instanceof ApiError) {
    if (e.status === 401 || e.status === 419) return "auth"
    if (e.status >= 500) return "server"
  }
  return "unknown"
}

/** Global 401 handler — wired by the auth bootstrap to force logout. */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn
}

export async function apiFetch<T>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const auth: AuthMode = opts.auth ?? "access"
  const headers: Record<string, string> = { Accept: "application/json" }

  if (auth === "registration") {
    const t = registrationTokenStore.get()
    if (t) headers.Authorization = `Bearer ${t}`
  } else if (auth === "access") {
    const t = accessTokenStore.get()
    if (t) headers.Authorization = `Bearer ${t}`
  }

  let body: BodyInit | undefined
  if (opts.formData) {
    body = opts.formData
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json"
    body = JSON.stringify(opts.body)
  }

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method: opts.method ?? "GET",
      headers,
      body,
      signal: opts.signal,
    })
  } catch (e) {
    // fetch only rejects on a network-level failure (offline/DNS/CORS). Preserve
    // aborts; otherwise surface a typed NetworkError so callers can say "offline".
    if (e instanceof DOMException && e.name === "AbortError") throw e
    leaveBreadcrumb(
      "API network error",
      { method: opts.method ?? "GET", path },
      "error"
    )
    throw new NetworkError(e instanceof Error ? e.message : undefined)
  }

  leaveBreadcrumb(
    "API request",
    { method: opts.method ?? "GET", path, status: res.status },
    "request"
  )

  // A 401 on an authenticated call means the session is dead — force logout.
  // Login/register 401s use auth:"none" and are surfaced to the form instead.
  if (res.status === 401 && auth !== "none") {
    onUnauthorized?.()
  }

  if (res.status === 204) return undefined as T

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    // Laravel's 429 body is untranslated English ("Too Many Attempts."), so
    // localize it here — once, for every screen — instead of per call site.
    const message =
      res.status === 429
        ? t("common.error.tooMany")
        : (data && typeof data.message === "string" && data.message) ||
          `Request failed (${res.status})`
    const code = data && typeof data.code === "string" ? data.code : undefined
    const apiError = new ApiError(res.status, message, code, data?.errors)
    // A suspended account on an authenticated call ends the session just like a
    // 401 — force logout. Login/register use auth:"none" and surface the
    // suspension to the form instead.
    if (res.status === 403 && auth !== "none" && code === SUSPENDED_CODE) {
      onUnauthorized?.()
    }
    // Surface real backend failures (5xx) to Bugsnag; 4xx (validation, auth,
    // not-found) are expected and handled by the UI, so they stay silent.
    if (res.status >= 500) notify(apiError)
    throw apiError
  }

  return data as T
}
